import { PropertyType } from "@/tailwind/tailwindUtils";
import { applyTailwindChangeToClasses } from "@/components/RightStyle/TailwindStylingPanel/utils/applyChanges";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";

// Step sizes for continuous adjustments (matches TailwindStylingPanelV2)
const ADJUSTMENT_STEPS = {
  [PropertyType.FONT_SIZE]: 0.25,
  [PropertyType.FONT_WEIGHT]: 100,
  [PropertyType.LETTER_SPACING]: 0.05,
  [PropertyType.LINE_HEIGHT]: 0.25,
} as const;

/**
 * Apply typography adjustment
 */
export const applyTypographyAdjustment = (
  elementClasses: string[],
  propertyType: PropertyType,
  direction: "increase" | "decrease",
): {
  newClasses: string[];
  involvedClasses: Array<{ tailwind: string; css: string }>;
} => {
  const tailwindClassesMap = useCodebaseStore.getState().tailwindClassesMap;
  const step = ADJUSTMENT_STEPS[propertyType];

  // Get current value from element classes
  const currentValue = extractCurrentValue(elementClasses, propertyType);
  const defaultValue = getDefaultValue(propertyType);
  const baseValue = currentValue ?? defaultValue;

  // Calculate new value
  const stepAmount = direction === "increase" ? step : -step;
  const newValue = baseValue + stepAmount;

  // Create arbitrary class option
  const option = createArbitraryOption(propertyType, newValue);

  const { newClasses, involvedClasses } = applyTailwindChangeToClasses({
    currentElementClasses: elementClasses,
    option,
    propertyType,
    variant: null,
  });

  return { newClasses, involvedClasses };
};

/**
 * Extract current value from element classes
 */
export const extractCurrentValue = (
  elementClasses: string[],
  propertyType: PropertyType,
): number | null => {
  const tailwindClassesMap = useCodebaseStore.getState().tailwindClassesMap;

  for (const className of elementClasses) {
    // Check arbitrary values
    const arbitraryMatch = className.match(/^(.+)-\[([^\]]+)\]$/);
    if (arbitraryMatch) {
      const [, prefix, value] = arbitraryMatch;
      if (isPropertyMatch(prefix, propertyType)) {
        const numericValue = parseNumericValue(value);
        if (numericValue !== null) return numericValue;
      }
    }

    // Check standard classes
    const matchedClass = tailwindClassesMap[className];
    if (matchedClass?.propertyType === propertyType) {
      const numericValue = parseNumericValue(matchedClass.css);
      if (numericValue !== null) return numericValue;
    }
  }

  return null;
};

/**
 * Check if class prefix matches property type
 */
const isPropertyMatch = (
  prefix: string,
  propertyType: PropertyType,
): boolean => {
  const prefixes = {
    [PropertyType.FONT_SIZE]: "text",
    [PropertyType.FONT_WEIGHT]: "font",
    [PropertyType.LETTER_SPACING]: "tracking",
    [PropertyType.LINE_HEIGHT]: "leading",
  };
  return prefixes[propertyType] === prefix;
};

/**
 * Parse numeric value from CSS or arbitrary value
 */
export const parseNumericValue = (value: string): number | null => {
  const match = value.match(/(\d+(?:\.\d+)?)/);
  if (match) {
    const num = parseFloat(match[1]);
    return isNaN(num) ? null : num;
  }
  return null;
};

/**
 * Get default value for property type
 */
const getDefaultValue = (propertyType: PropertyType): number => {
  const defaults = {
    [PropertyType.FONT_SIZE]: 16,
    [PropertyType.FONT_WEIGHT]: 400,
    [PropertyType.LETTER_SPACING]: 0,
    [PropertyType.LINE_HEIGHT]: 1.5,
  };
  return defaults[propertyType];
};

/**
 * Create arbitrary option
 */
export const createArbitraryOption = (
  propertyType: PropertyType,
  value: number,
) => {
  const classPrefixes = {
    [PropertyType.FONT_SIZE]: "text",
    [PropertyType.FONT_WEIGHT]: "font",
    [PropertyType.LETTER_SPACING]: "tracking",
    [PropertyType.LINE_HEIGHT]: "leading",
  };

  const units = {
    [PropertyType.FONT_SIZE]: "px",
    [PropertyType.FONT_WEIGHT]: "",
    [PropertyType.LETTER_SPACING]: "em",
    [PropertyType.LINE_HEIGHT]: "",
  };

  const prefix = classPrefixes[propertyType];
  const unit = units[propertyType];
  const formattedValue =
    propertyType === PropertyType.FONT_WEIGHT
      ? Math.round(value)
      : value.toFixed(propertyType === PropertyType.LETTER_SPACING ? 3 : 2);

  const tailwind = `${prefix}-[${formattedValue}${unit}]`;
  const css = generateCss(propertyType, value);

  return { tailwind, css };
};

/**
 * Generate CSS for property
 */
const generateCss = (propertyType: PropertyType, value: number): string => {
  const cssProps = {
    [PropertyType.FONT_SIZE]: `font-size: ${value}px;`,
    [PropertyType.FONT_WEIGHT]: `font-weight: ${Math.round(value)};`,
    [PropertyType.LETTER_SPACING]: `letter-spacing: ${value}em;`,
    [PropertyType.LINE_HEIGHT]: `line-height: ${value};`,
  };
  return cssProps[propertyType];
};
